import React, { useState } from "react";
import styled from "styled-components";
import {
  colors2,
  TextInput,
  UI_BODY_SEMIBOLD_SM_DF,
  UI_BODY_SM_DF,
  UI_CAPTION,
  LoaderOverlay,
} from "oolib";
import { ButtonPrimary, ButtonSecondary } from "oolib/dist/v2/components/Buttons";
import { useFetchPreflightData } from "../../hooks/useFetchPreflightData";

// ============================================
// STYLED COMPONENTS
// ============================================

const InterfaceContainer = styled.div`
  padding: 20px;
  border: 1px solid ${colors2.grey10};
  border-radius: 8px;
  background: ${colors2.grey5};
`;

const InterfaceHeader = styled(UI_BODY_SEMIBOLD_SM_DF)`
  margin-bottom: 12px;
`;

const InterfaceDescription = styled(UI_BODY_SM_DF)`
  margin-bottom: 16px;
  color: ${colors2.grey60};
`;

const InterfaceActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
`;

const HelpText = styled(UI_CAPTION)`
  color: ${colors2.grey60};
  margin-top: 8px;

  a {
    color: ${colors2.blue};
    text-decoration: underline;
  }
`;

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const StepDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$active ? colors2.blue : colors2.grey20};
`;

const StepLabel = styled(UI_CAPTION)`
  color: ${props => props.$active ? colors2.grey80 : colors2.grey40};
`;

const ErrorMessage = styled(UI_CAPTION)`
  color: ${colors2.red};
  margin-top: 8px;
`;

const PreflightSelectorWrapper = styled.div`
  margin-top: 16px;
`;

// ============================================
// BASE PREFLIGHT INTERFACE COMPONENT
// ============================================

/**
 * BasePreflightInterface
 *
 * A reusable base component for adapters that need a two-step configuration flow.
 *
 * Flow:
 * 1. User enters API key → clicks "Connect"
 * 2. Preflight data is fetched (e.g., list of channels)
 * 3. User selects from the preflight data (e.g., picks a channel)
 * 4. User clicks "Import" → onSubmit is called with apiKey and adapterOptions
 *
 * ============================================
 * PROPS:
 * ============================================
 *
 * @param {Object} adapterConfig - Config from EXTERNAL_IMPORT_ADAPTERS_CONFIG
 * @param {Function} onSubmit - Called with { apiKey, adapterOptions } when user completes
 * @param {boolean} isLoading - Shows loading state on final submit button
 * @param {Function} renderPreflightSelector - Custom render function for the selection UI
 *   Signature: (preflightData, selectedValue, onChange) => ReactNode
 * @param {Function} getAdapterOptionsFromSelection - Convert selection to adapterOptions
 *   Signature: (selectedValue) => Object
 * @param {Function} isSelectionValid - Optional custom validation for selection
 *   Signature: (selectedValue) => boolean
 *   Default: checks if selectedValue is truthy
 * @param {Object} customLabels - Optional custom labels for buttons/text
 */
export const BasePreflightInterface = ({
  adapterConfig,
  onSubmit,
  isLoading,
  renderPreflightSelector,
  getAdapterOptionsFromSelection,
  isSelectionValid,
  customLabels = {},
}) => {
  const [apiKey, setApiKey] = useState("");
  const [preflightData, setPreflightData] = useState(null);
  const [selectedValue, setSelectedValue] = useState(null);
  const [error, setError] = useState(null);

  const {
    mutate: fetchPreflight,
    isLoading: isPreflightLoading,
  } = useFetchPreflightData();

  // Determine which step we're on
  const currentStep = preflightData ? 2 : 1;

  // Labels with defaults
  const labels = {
    connectButton: customLabels.connectButton || "Connect",
    submitButton: customLabels.submitButton || adapterConfig?.preflightConfig?.submitLabel || "Import Data",
    step1Label: customLabels.step1Label || "Enter credentials",
    step2Label: customLabels.step2Label || "Select options",
    ...customLabels,
  };

  const handleConnect = () => {
    if (!apiKey.trim()) return;
    setError(null);

    fetchPreflight(
      { sourceId: adapterConfig.id, apiKey: apiKey.trim() },
      {
        onSuccess: (data) => {
          setPreflightData(data.preflightData);
        },
        onError: (err) => {
          setError(err.message || "Failed to connect. Please check your credentials.");
        },
      }
    );
  };

  const handleSubmit = () => {
    // Use custom validation if provided
    const isValid = isSelectionValid
      ? isSelectionValid(selectedValue)
      : Boolean(selectedValue);

    if (!isValid) return;

    onSubmit({
      apiKey: apiKey.trim(),
      adapterOptions: getAdapterOptionsFromSelection(selectedValue),
    });
  };

  const handleBack = () => {
    setPreflightData(null);
    setSelectedValue(null);
    setError(null);
  };

  return (
    <InterfaceContainer>
      {/* Full-page loader during preflight fetch */}
      {isPreflightLoading && (
        <LoaderOverlay loaderText="Connecting..." />
      )}

      <InterfaceHeader>
        Connect to {adapterConfig.name}
      </InterfaceHeader>
      <InterfaceDescription>
        {adapterConfig.description}
      </InterfaceDescription>

      {/* Step Indicator */}
      <StepIndicator>
        <StepDot $active={currentStep >= 1} />
        <StepLabel $active={currentStep === 1}>{labels.step1Label}</StepLabel>
        <StepDot $active={currentStep >= 2} />
        <StepLabel $active={currentStep === 2}>{labels.step2Label}</StepLabel>
      </StepIndicator>

      {/* Step 1: API Key Input */}
      {currentStep === 1 && (
        <>
          <TextInput
            label={adapterConfig.authConfig?.keyLabel || "API Key"}
            placeholder={adapterConfig.authConfig?.keyPlaceholder || "Enter your API key"}
            value={apiKey}
            onChange={(k, v) => setApiKey(v)}
            type="password"
          />

          {adapterConfig.authConfig?.helpText && (
            <HelpText>
              {adapterConfig.authConfig.helpText}
              {adapterConfig.authConfig?.helpUrl && (
                <>
                  {" "}
                  <a
                    href={adapterConfig.authConfig.helpUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Learn more
                  </a>
                </>
              )}
            </HelpText>
          )}

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <InterfaceActions>
            <ButtonPrimary
              onClick={handleConnect}
              disabled={!apiKey.trim() || isPreflightLoading}
              isLoading={isPreflightLoading}
            >
              {labels.connectButton}
            </ButtonPrimary>
          </InterfaceActions>
        </>
      )}

      {/* Step 2: Preflight Selection */}
      {currentStep === 2 && preflightData && (
        <>
          <PreflightSelectorWrapper>
            {renderPreflightSelector(preflightData, selectedValue, setSelectedValue)}
          </PreflightSelectorWrapper>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <InterfaceActions>
            <ButtonSecondary onClick={handleBack} disabled={isLoading}>
              Back
            </ButtonSecondary>
            <ButtonPrimary
              onClick={handleSubmit}
              disabled={
                // Use custom validation if provided, otherwise default to checking selectedValue
                isSelectionValid
                  ? !isSelectionValid(selectedValue)
                  : !selectedValue
                || isLoading
              }
              isLoading={isLoading}
            >
              {labels.submitButton}
            </ButtonPrimary>
          </InterfaceActions>
        </>
      )}
    </InterfaceContainer>
  );
};

// Export styled components for custom interfaces to use
export {
  InterfaceContainer,
  InterfaceHeader,
  InterfaceDescription,
  InterfaceActions,
  HelpText,
  StepIndicator,
  StepDot,
  StepLabel,
  ErrorMessage,
  PreflightSelectorWrapper,
};

export default BasePreflightInterface;
